import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { Actions, ofType } from '@ngrx/effects';
import { APP_ENVIRONMENT } from '@qtek/core/api-core';
import { AuthActions, IntegrationsActions, UserStore } from '@qtek/libs/store';
import { AccountOutput, PersonOutput } from '@qtek/shared/models';

const INTERCOM_TOKEN_KEY = 'intercom_user_jwt';

@Injectable({ providedIn: 'root' })
export class IntercomService {
  private readonly actions$ = inject(Actions);
  private readonly userStore = inject(UserStore);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    const appId = this.environment.intercomAppId;
    if (!this.environment.intercomEnabled || !appId) {
      return;
    }

    this.actions$
      .pipe(
        ofType(IntegrationsActions.getIntercomTokenSuccess),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ token }) => {
        const prs = this.userStore.currentPerson();
        const acn = this.userStore.mainCompany();
        if (!prs || !token) return;
        this.initIntercom(appId, token, prs, acn);
      });

    this.actions$
      .pipe(ofType(AuthActions.logout), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        sessionStorage.removeItem(INTERCOM_TOKEN_KEY);
        shutdown();
      });
  }

  private initIntercom(
    appId: string,
    token: string,
    prs: PersonOutput,
    acn: AccountOutput | null
  ): void {
    Intercom({
      app_id: appId,
      user_id: prs.usrId,
      name: `${prs.fname ?? ''} ${prs.lname ?? ''}`.trim() || prs.name,
      email: prs.email,
      ...(acn?.createTs ? { created_at: Math.floor(acn.createTs / 1000) } : {}),
      intercom_user_jwt: token,
    });
  }
}
